import React from "react";
import * as Sentry from "@sentry/react";

/**
 * Fatal-error fallback for the top-level {@link ErrorBoundary}.
 *
 * Rendered with plain elements and inline styles only — no theme, component library, or
 * app providers — so it still displays when those providers are unavailable or are
 * themselves the source of the error, instead of leaving a blank page.
 */
const overlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  background: "#ffffff",
  color: "#1a2233",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const cardStyle: React.CSSProperties = {maxWidth: 480, textAlign: "center"};
const titleStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 600,
  margin: "0 0 12px",
};
const textStyle: React.CSSProperties = {
  fontSize: 16,
  lineHeight: 1.5,
  margin: "0 0 24px",
  color: "#4a5568",
};
const reloadStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 6,
  padding: "10px 20px",
  fontSize: 16,
  cursor: "pointer",
  background: "#2b6cb0",
  color: "#ffffff",
  margin: "0 6px",
};
const reportStyle: React.CSSProperties = {
  border: "none",
  background: "none",
  color: "#2b6cb0",
  fontSize: 15,
  cursor: "pointer",
  textDecoration: "underline",
  margin: "0 6px",
};

/**
 * The full-screen fallback UI shown by the error boundary.
 *
 * @returns {JSX.Element} A dependency-free error screen with a reload action.
 */
export const ErrorBanner = (): JSX.Element => (
  <div style={overlayStyle}>
    <div style={cardStyle}>
      <h1 style={titleStyle}>Oops, an error occurred.</h1>
      <p style={textStyle}>
        Please reload the page. If the problem continues, make sure your browser and
        operating system are up to date, or try a different device.
      </p>
      <button
        type="button"
        style={reloadStyle}
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
      {import.meta.env.VITE_SENTRY_DSN && (
        <button
          type="button"
          style={reportStyle}
          onClick={() => Sentry.showReportDialog()}
        >
          Report feedback
        </button>
      )}
    </div>
  </div>
);
